import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer,
  Box,
  Divider,
  Container,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  useTheme,
  Button,
} from '@mui/material';
import { TableLoader, TableNotFound } from 'src/Utils/TableLoader';
import axios from 'axios';
import { url, imgUrl } from 'src/Helpers/utils/Constant';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import GetDataApi from 'src/common/FetchData/GetDataApi';
import { useSearchParams, Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Excel from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';

const DueReport = () => {
  const theme = useTheme();

  // Filters state
  const [selectedZone, setSelectedZone] = useState<string | number>('');
  const [date, setDate] = useState<{
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }>({
    startDate: dayjs('2023-01-01'),
    endDate: dayjs('2026-01-01'),
  });

  const [searchInput, setSearchInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [filterItem, setFilterItem] = useState<{ zone: any[] }>({
    zone: [],
  });

  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    limit: '25',
  });

  const page = parseInt(searchParams.get('page') || '1');
  const limit = parseInt(searchParams.get('limit') || '25');

  // Fetch zones on mount
  useEffect(() => {
    (async function () {
      try {
        const { data } = await axios.get(`${url}/api/atab/common/get/all/zone`);
        if (data.success) {
          setFilterItem({ zone: data.data });
        }
      } catch (error) {
        console.error('Error fetching zones:', error);
      }
    })();
  }, []);

  // Debounced search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const fromDateStr = date.startDate
    ? dayjs(date.startDate).format('YYYY-MM-DD')
    : '';
  const toDateStr = date.endDate
    ? dayjs(date.endDate).format('YYYY-MM-DD')
    : '';
  const skip = limit * (page - 1);

  const { response, loading, total, getData } = GetDataApi<any[]>(
    `/api/atab/member/report/due-report?zone=${selectedZone}&from_date=${fromDateStr}&to_date=${toDateStr}&skip=${skip}&limit=${limit}&searchParams=${searchQuery}`,
    true,
  );

  useEffect(() => {
    getData();
  }, [selectedZone, date.startDate, date.endDate, page, limit, searchQuery]);

  const handleZoneChange = (e: any) => {
    setSelectedZone(e.target.value);
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const handleDateChange = (
    key: 'startDate' | 'endDate',
    val: Dayjs | null,
  ) => {
    setDate((prev) => ({ ...prev, [key]: val }));
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const handlePageChange = (event: any, newPage: number): void => {
    searchParams.set('page', (newPage + 1).toString());
    setSearchParams(searchParams);
  };

  const handleLimitChange = (event: any): void => {
    searchParams.set('limit', event.target.value);
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const fetchAllDataForDownload = async () => {
    try {
      const { data } = await axios.get(
        `${url}/api/atab/member/report/due-report?zone=${selectedZone}&from_date=${fromDateStr}&to_date=${toDateStr}&searchParams=${searchQuery}`,
      );
      if (data.success) {
        return data.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching data for download:', error);
      return [];
    }
  };

  const downloadExcel = async () => {
    setExcelLoading(true);
    try {
      const excelData = await fetchAllDataForDownload();
      const workbook = new Excel.Workbook();
      const worksheet = workbook.addWorksheet('Member Dues');

      const excelTableHead = [
        'SL',
        'Membership No',
        'Photo',
        'Company Name',
        'Representative Name',
        'Designation',
        'Last Payment Date',
        'Zone',
        'Due Years',
        'Total Due',
      ];

      const titleRow = worksheet.addRow(excelTableHead);
      worksheet.columns = [
        { width: 10 },
        { width: 20 },
        { width: 15 },
        { width: 35 },
        { width: 25 },
        { width: 20 },
        { width: 20 },
        { width: 20 },
        { width: 30 },
        { width: 20 },
      ];

      titleRow.eachCell((cell) => {
        cell.font = { bold: true };
      });

      for (let i = 0; i < excelData.length; i++) {
        const item = excelData[i];
        const rowData = [
          i + 1,
          item.membership || item.user_member_id,
          '', // Photo placeholder
          item.user_member_company_name,
          item.company_owner_name || 'N/A',
          item.company_owner_designation || 'N/A',
          item.last_payment_date
            ? dayjs(item.last_payment_date).format('DD-MMM-YYYY')
            : 'N/A',
          item.zone || 'N/A',
          item.due_year || 'N/A',
          item.total_due || '0.00',
        ];

        const row = worksheet.addRow(rowData);
        row.height = 60;
        row.alignment = { vertical: 'middle', horizontal: 'left' };

        if (item.company_owner_photo) {
          try {
            const response = await fetch(
              `${imgUrl}/${item.company_owner_photo}`,
            );
            const blob = await response.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const extension =
              item.company_owner_photo.split('.').pop() || 'jpeg';
            const imageId = workbook.addImage({
              buffer: arrayBuffer,
              extension: (extension === 'jpg' ? 'jpeg' : extension) as any,
            });
            worksheet.addImage(imageId, {
              tl: { col: 2, row: i + 1 },
              ext: { width: 50, height: 50 },
            });
          } catch (error) {
            console.error('Error adding image to excel:', error);
          }
        }
      }

      const excelDataGenerate = await workbook.xlsx.writeBuffer();
      const blob = new Blob([excelDataGenerate], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `member_due_report.xlsx`);
    } catch (error: any) {
      console.error(
        'Something went wrong during Excel download:',
        error.message,
      );
    } finally {
      setExcelLoading(false);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const pdfData = await fetchAllDataForDownload();
      const doc = new jsPDF({
        orientation: 'portrait',
        format: 'a4',
      });

      doc.setFontSize(10);
      doc.text(`Print Date: ${dayjs().format('DD-MMM-YYYY')}`, 14, 20, {
        align: 'left',
      });

      doc.text('Member Due Report', doc.internal.pageSize.getWidth() / 2, 12, {
        align: 'center',
        renderingMode: 'fill',
      });

      const tableHeaders = [
        'SL',
        'Membership No',
        'Company Name',
        'Representative Name',
        'Designation',
        'Last Payment Date',
        'Zone',
        'Due Years',
        'Total Due',
      ];

      const tableRows = pdfData.map((item: any, index: number) => [
        index + 1,
        item.membership || item.user_member_id,
        item.user_member_company_name,
        item.company_owner_name || 'N/A',
        item.company_owner_designation || 'N/A',
        item.last_payment_date
          ? dayjs(item.last_payment_date).format('DD-MMM-YYYY')
          : 'N/A',
        item.zone || 'N/A',
        item.due_year || 'N/A',
        item.total_due || '0.00',
      ]);

      autoTable(doc, {
        styles: { halign: 'center', fontSize: 7.5 },
        headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
        startY: 25,
        head: [tableHeaders],
        body: tableRows,
        theme: 'grid',
        didDrawPage: ({ pageNumber }) => {
          doc.setFontSize(8);
          doc.text(
            `Page - ${pageNumber}`,
            doc.internal.pageSize.getWidth() / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' },
          );
        },
      });

      doc.save(`member_due_report.pdf`);
    } catch (error: any) {
      console.error('Error generating PDF:', error.message);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <Container sx={{ mt: 4 }} maxWidth='xl'>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <Grid
              container
              spacing={2}
              alignItems='center'
              justifyContent='space-between'
            >
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  id='search-agency'
                  label='Search by agency name'
                  variant='outlined'
                  value={searchInput}
                  onChange={handleSearchChange}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label='Start Date'
                  value={date.startDate}
                  format='DD/MM/YYYY'
                  onChange={(val) => handleDateChange('startDate', val)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label='End Date'
                  value={date.endDate}
                  format='DD/MM/YYYY'
                  onChange={(val) => handleDateChange('endDate', val)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel id='zone-select-label'>Zone</InputLabel>
                  <Select
                    labelId='zone-select-label'
                    id='zone-select'
                    value={selectedZone}
                    label='Zone'
                    onChange={handleZoneChange}
                  >
                    <MenuItem value=''>All Zones</MenuItem>
                    {filterItem.zone.map((sZone) => (
                      <MenuItem value={sZone.zone_id} key={sZone.zone_id}>
                        {sZone.zone_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={`Member Due Report (${total || 0})`}
              action={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Button
                    onClick={downloadExcel}
                    variant='outlined'
                    size='small'
                    disabled={excelLoading}
                  >
                    {excelLoading ? 'Downloading...' : 'Download Excel'}
                  </Button>
                  <Button
                    onClick={downloadPdf}
                    variant='outlined'
                    size='small'
                    disabled={pdfLoading}
                  >
                    {pdfLoading ? 'Downloading...' : 'Download PDF'}
                  </Button>
                </Box>
              }
            />
            <Divider />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        fontWeight: 'bold',
                        fontSize: '12px !important',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                      },
                    }}
                  >
                    <TableCell>SL</TableCell>
                    <TableCell>Membership No</TableCell>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Representative Name</TableCell>
                    <TableCell>Last Payment Date</TableCell>
                    <TableCell>Zone</TableCell>
                    <TableCell>Due Years</TableCell>
                    <TableCell>Total Due</TableCell>
                    <TableCell align='right'>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableLoader />
                  ) : response && response.length > 0 ? (
                    response.map((item: any, index: number) => (
                      <TableRow key={item.user_member_id}>
                        <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                        <TableCell>
                          {item.membership || item.user_member_id}
                        </TableCell>
                        <TableCell>{item.user_member_company_name}</TableCell>
                        <TableCell>
                          {item.company_owner_name || 'N/A'}
                        </TableCell>

                        <TableCell>
                          {item.last_payment_date
                            ? dayjs(item.last_payment_date).format(
                                'DD-MMM-YYYY',
                              )
                            : 'N/A'}
                        </TableCell>
                        <TableCell>{item.zone || 'N/A'}</TableCell>
                        <TableCell>{item.due_year || 'N/A'}</TableCell>
                        <TableCell>{item.total_due}</TableCell>
                        <TableCell align='right'>
                          <Link to={`/member-list/${item.user_member_id}`}>
                            <Tooltip title='View Profile' arrow>
                              <IconButton
                                sx={{
                                  '&:hover': {
                                    background: theme.colors.primary.lighter,
                                  },
                                  color: theme.palette.primary.main,
                                }}
                                color='inherit'
                                size='small'
                              >
                                <VisibilityIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableNotFound />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box p={2}>
              <TablePagination
                component='div'
                count={total || 0}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
                page={page - 1}
                rowsPerPage={limit}
                rowsPerPageOptions={[25, 50, 75, 100]}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DueReport;
